<template>
  <div>
    <v-data-table
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-options': [10, 20, 30, 40, 50],
        'show-first-last-page': true,
      }"
      :headers="headers"
      :items="providers"
      must-sort
    >

    <template #top>
      <v-toolbar flat color="white">
        <v-spacer />

        <button
          class="default-btn blue"
          @click="createProvider()">
          New Provider
        </button>
      </v-toolbar>
    </template>

      <template #item.actions="{ item }">
        <router-link
          class="default-btn minimal subdued-hover green"
          :to="{ name: 'AdminProviderDetails', params: { providerId: item.id }}">
          Details
        </router-link>
        <button
          class="default-btn minimal blue ml-2"
          @click="editProvider(item)">
          <v-icon color="inherit">mdi-pen</v-icon>
        </button>
        <button
          class="default-btn red minimal subdued-hover ml-2"
          @click="removeProvider(item)">
          <v-icon color="inherit">mdi-delete</v-icon>
        </button>
      </template>

      <template #item.type="{ value }">
        {{ getTypeName(value) }}
      </template>

      <template #item.reviewScore="{ value }">
        <StarRating
        :rating="value"
        :editable="false"
        :size="20" justify="flex-start" />
      </template>

      <template #item.createdAt="{ value }">
        {{ value | date }}
      </template>
    </v-data-table>

    <CreateEditProvider
      v-if="!!createEditProvider"
      :originalProvider="createEditProvider"
      :editing="editing"
      :saving="saving"
      @cancel="createEditProvider = null; editing = false"
      @submit="editing ? editProvider($event, true) : createProvider($event)"/>

    <ConfirmRemoveItem
      v-if="!!confirmRemoveProvider"
      itemName="provider"
      :saving="saving"
      @cancel="confirmRemoveProvider = null"
      @confirm="removeProvider(confirmRemoveProvider, true)" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import ConfirmRemoveItem from '../../components/ConfirmRemoveItem'
import CreateEditProvider from '../../components/CreateEditProvider'

export default {
  name: 'AdminProvidersList',

  components: {
    ConfirmRemoveItem,
    CreateEditProvider,
  },

  data: () => ({
    loading: true,
    saving: false,
    editing: false,
    providers: [],

    createEditProvider: null,
    confirmRemoveProvider: null,

    headers: [
      { text: '', value: 'actions', sortable: false },
      { text: 'Type', value: 'type' },
      { text: 'Company Name', value: 'companyName' },
      { text: 'Contact Name', value: 'contactName' },
      { text: 'Review Score', value: 'reviewScore' },
      { text: 'Review Count', value: 'reviewCount' },
      { text: 'Created At', value: 'createdAt' },
    ],

    options: {
      itemsPerPage: 10,
      sortBy: ['createdAt'],
      sortDesc: [true],
      page: 1,
    },
    totalItems: 0,
  }),

  computed: {
    ...mapGetters('feathers', ['apiProviders']),
  },

  created () {
    this.getProviders()
  },

  methods: {
    async getProviders () {
      this.loading = true

      const query = {
        $limit: this.options.itemsPerPage || 10,
        $skip: (this.options.itemsPerPage || 10) * (this.options.page - 1),
      }

      try {
        const result = await this.apiProviders.find({ query })
        this.providers = result.data
        this.totalItems = result.total
      } catch (error) {
        this.addErrorAlert(`Could not retrieve providers: ${error.message}`)
      }

      this.loading = false
    },

    async createProvider (provider = null) {
      if (!provider) {
        this.createEditProvider = {}
        return
      }

      try {
        this.saving = true

        await this.apiProviders.create(provider)
        await this.getProviders()

        this.createEditProvider = null
      } catch (error) {
        this.addErrorAlert(`Could not create provider: ${error.message}`)
      }

      this.saving = false
    },

    async editProvider (provider, confirmed = false) {
      if (!confirmed) {
        this.createEditProvider = provider
        this.editing = true
        return
      }

      try {
        this.saving = true

        await this.apiProviders.patch(provider.id, provider)
        await this.getProviders()

        this.createEditProvider = null
        this.editing = false
      } catch (error) {
        this.addErrorAlert(`Could not update provider: ${error.message}`)
      }

      this.saving = false
    },

    async removeProvider (provider, confirmed = false) {
      if (!confirmed) {
        this.confirmRemoveProvider = provider
        return
      }

      try {
        this.saving = true

        await this.apiProviders.remove(provider.id)
        await this.getProviders()

        this.confirmRemoveProvider = null
      } catch (error) {
        this.addErrorAlert(`Could not delete provider: ${error.message}`)
      }

      this.saving = false
    },

    getTypeName (type) {
      switch (type) {
        case 'attorney': return 'Attorney'
        case 'compliance': return 'Compliance Consultant'
        case 'cpa': return 'CPA'
        case 'eo': return 'E&O Provider'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.default-btn.minimal {
  height: 30px !important;
}
</style>
